// Eingabe je Feldtyp (Custom Fields am Lead/Kontakt und Felder der Custom Activities):
// Text, Textfeld, Zahl, Datum, Datum+Uhrzeit, Auswahl einfach/mehrfach, Nutzer, Kontakt.
// Auswahl-, Nutzer- und Kontaktfelder sind immer Dropdowns; Datum und Uhrzeit öffnen beim Klick den Picker.
import { ZeitFeld } from "./ZeitFeld";
import { useEffect, useRef, useState } from "react";
import type { FeldWert, Kontakt } from "../api";
import { useMeta } from "../daten/meta";
import { alsListe, istLeer, zahlLesen, zeitpunktTeile, type FeldDef } from "../werkzeuge/felder";
import { htmlZuText, istHtml } from "../werkzeuge/html";
import { berlinZuIso, heute } from "../werkzeuge/zeit";
import { Auswahl, type Option } from "./Auswahl";

interface Props {
  def: FeldDef;
  wert: FeldWert | undefined;
  onChange: (w: FeldWert) => void;
  kontakte?: Kontakt[];
  /** formular: im Formular; inline: kompakt, Dropdowns öffnen sofort (Bearbeiten in der linken Spalte) */
  darstellung?: "formular" | "inline";
  autoFokus?: boolean;
  /** Enter in einzeiligen Feldern bzw. Auswahl getroffen (Inline-Bearbeitung speichert dann) */
  onFertig?: (w?: FeldWert) => void;
  onAbbrechen?: () => void;
  fehler?: boolean;
  id?: string;
}

export function FeldEingabe({ def, wert, onChange, kontakte = [], darstellung = "formular", autoFokus, onFertig, onAbbrechen, fehler, id }: Props) {
  const { nutzerListe } = useMeta();
  const typ = def.type;
  const klasse = `eingabe${fehler ? " eingabe--fehler" : ""}`;
  const tasten = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" && onAbbrechen) {
      e.preventDefault();
      e.stopPropagation();
      onAbbrechen();
    } else if (e.key === "Enter" && onFertig && !(e.target instanceof HTMLTextAreaElement)) {
      e.preventDefault();
      onFertig();
    }
  };

  if (typ === "choices") {
    // Auswahlfelder (einfach und mehrfach) immer als Dropdown wie in Close: nummeriert, Ziffer wählt direkt
    const choices = def.choices ?? [];
    const mehrfach = def.accepts_multiple_values;
    const optionen: Option[] = choices.map((c) => ({ wert: c, label: c }));
    return (
      <Auswahl
        id={id}
        optionen={optionen}
        wert={mehrfach ? alsListe(wert) : typeof wert === "string" ? wert : null}
        mehrfach={mehrfach}
        leerErlaubt
        nummeriert
        pflicht={def.required}
        platzhalter={mehrfach ? "Eine oder mehrere wählen …" : "Auswählen …"}
        autoOeffnen={darstellung === "inline" && autoFokus}
        knopfKlasse={`auswahl__knopf${fehler ? " auswahl__knopf--fehler" : ""}`}
        onWahl={(w) => {
          const neu = mehrfach ? (w as string[]) : (w as string | null);
          onChange(neu);
          if (!mehrfach) onFertig?.(neu);
        }}
        onGeschlossen={() => {
          if (mehrfach) onFertig?.();
          else if (darstellung === "inline") onAbbrechen?.();
        }}
      />
    );
  }

  if (typ === "user") {
    const mehrfach = def.accepts_multiple_values;
    const gewaehlt = new Set(alsListe(wert));
    const optionen: Option[] = nutzerListe
      .filter((u) => u.is_active || gewaehlt.has(u.id) || (u.close_user_id && gewaehlt.has(u.close_user_id)))
      .map((u) => ({ wert: u.close_user_id ?? u.id, label: u.name, unter: u.is_active ? null : "inaktiv" }));
    return (
      <Auswahl
        id={id}
        optionen={optionen}
        wert={mehrfach ? alsListe(wert) : typeof wert === "string" ? wert : null}
        mehrfach={mehrfach}
        leerErlaubt
        platzhalter="Nutzer wählen …"
        pflicht={def.required}
        autoOeffnen={darstellung === "inline" && autoFokus}
        knopfKlasse={`auswahl__knopf${fehler ? " auswahl__knopf--fehler" : ""}`}
        onWahl={(w) => {
          onChange(w as FeldWert);
          if (!mehrfach) onFertig?.(w as FeldWert);
        }}
        onGeschlossen={() => {
          if (mehrfach) onFertig?.();
          else if (darstellung === "inline") onAbbrechen?.();
        }}
      />
    );
  }

  if (typ === "contact") {
    const mehrfach = def.accepts_multiple_values;
    const optionen: Option[] = kontakte.map((k) => ({ wert: k.id, label: k.name || k.display_name || "Ohne Namen", unter: k.title }));
    return (
      <Auswahl
        id={id}
        optionen={optionen}
        wert={mehrfach ? alsListe(wert) : typeof wert === "string" ? wert : null}
        mehrfach={mehrfach}
        leerErlaubt
        platzhalter={kontakte.length ? "Kontakt wählen …" : "Lead hat keine Kontakte"}
        pflicht={def.required}
        deaktiviert={!kontakte.length}
        autoOeffnen={darstellung === "inline" && autoFokus}
        knopfKlasse={`auswahl__knopf${fehler ? " auswahl__knopf--fehler" : ""}`}
        onWahl={(w) => {
          onChange(w as FeldWert);
          if (!mehrfach) onFertig?.(w as FeldWert);
        }}
        onGeschlossen={() => {
          if (mehrfach) onFertig?.();
          else if (darstellung === "inline") onAbbrechen?.();
        }}
      />
    );
  }

  if (typ === "number") return <ZahlEingabe id={id} klasse={klasse} wert={wert} onChange={onChange} autoFokus={autoFokus} onKeyDown={tasten} />;

  if (typ === "date") {
    return (
      <input
        id={id}
        className={`${klasse} eingabe--datum`}
        type="date"
        value={typeof wert === "string" ? wert.slice(0, 10) : ""}
        autoFocus={autoFokus}
        required={def.required}
        onClick={pickerOeffnen}
        onChange={(e) => onChange(e.target.value || null)}
        onKeyDown={tasten}
      />
    );
  }

  if (typ === "datetime") return <ZeitpunktEingabe id={id} klasse={klasse} wert={wert} onChange={onChange} autoFokus={autoFokus} onKeyDown={tasten} />;

  if (typ === "textarea" || typ === "richtextarea") {
    const text = typeof wert === "string" ? (istHtml(wert) ? htmlZuText(wert) : wert) : "";
    return (
      <textarea
        id={id}
        className={`${klasse} eingabe--text`}
        rows={3}
        value={text}
        autoFocus={autoFokus}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape" && onAbbrechen) {
            e.preventDefault();
            onAbbrechen();
          } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && onFertig) {
            e.preventDefault();
            onFertig();
          }
        }}
      />
    );
  }

  if (typ === "hidden" || typ === "custom_object") {
    return <span className="feld__nurlesen">{istLeer(wert) ? "—" : alsListe(wert).join(", ")}</span>;
  }

  return (
    <input
      id={id}
      className={klasse}
      value={typeof wert === "string" || typeof wert === "number" ? String(wert) : ""}
      autoFocus={autoFokus}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={tasten}
    />
  );
}

/** Datum/Uhrzeit: Klick öffnet sofort den Kalender bzw. die Uhrzeitliste des Browsers (wie Close). */
export function pickerOeffnen(e: React.MouseEvent<HTMLInputElement>) {
  try {
    e.currentTarget.showPicker?.();
  } catch {
    /* manche Umgebungen erlauben es nicht, dann bleibt das normale Feld */
  }
}

function ZahlEingabe({ id, klasse, wert, onChange, autoFokus, onKeyDown }: { id?: string; klasse: string; wert: FeldWert | undefined; onChange: (w: FeldWert) => void; autoFokus?: boolean; onKeyDown: (e: React.KeyboardEvent) => void }) {
  const [text, setText] = useState(() => (istLeer(wert) ? "" : String(wert).replace(".", ",")));
  useEffect(() => {
    const aktuell = zahlLesen(text);
    const extern = istLeer(wert) ? null : Number(wert);
    if (aktuell !== extern && !(Number.isNaN(aktuell) && typeof wert === "number" && Number.isNaN(wert))) {
      setText(extern === null ? "" : String(extern).replace(".", ","));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wert]);
  return (
    <input
      id={id}
      className={`${klasse} eingabe--zahl`}
      inputMode="decimal"
      value={text}
      autoFocus={autoFokus}
      onChange={(e) => {
        setText(e.target.value);
        onChange(zahlLesen(e.target.value));
      }}
      onKeyDown={onKeyDown}
    />
  );
}

function ZeitpunktEingabe({ id, klasse, wert, onChange, autoFokus, onKeyDown }: { id?: string; klasse: string; wert: FeldWert | undefined; onChange: (w: FeldWert) => void; autoFokus?: boolean; onKeyDown: (e: React.KeyboardEvent) => void }) {
  const [teile, setTeile] = useState(() => zeitpunktTeile(wert));
  const letzter = useRef<FeldWert | undefined>(wert);
  useEffect(() => {
    if (wert !== letzter.current) {
      setTeile(zeitpunktTeile(wert));
      letzter.current = wert;
    }
  }, [wert]);
  const setzen = (datum: string, zeit: string) => {
    // Uhrzeit zuerst getippt: der Tag ist dann heute (statt dass die Eingabe verloren geht)
    if (zeit && !datum) datum = heute();
    setTeile({ datum, zeit });
    const neu: FeldWert = datum && zeit ? berlinZuIso(datum, zeit) : datum ? datum : null;
    letzter.current = neu;
    onChange(neu);
  };
  return (
    <span className="zeitpunkt">
      <input id={id} className={`${klasse} eingabe--datum`} type="date" value={teile.datum} autoFocus={autoFokus} onClick={pickerOeffnen} onChange={(e) => setzen(e.target.value, teile.zeit)} onKeyDown={onKeyDown} aria-label="Datum" />
      {/* ungültige Uhrzeit = nur der Tag geht raus; die Pflichtprüfung meldet dann „Bitte auch die Uhrzeit wählen“ */}
      <ZeitFeld className={`${klasse} eingabe--uhrzeit`} wert={teile.zeit} onChange={(z) => setzen(teile.datum, z)} onKeyDown={onKeyDown} beiUngueltig="leer" />
    </span>
  );
}
